import styled from "styled-components";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ScheduleHourPicker from "../pages/loggedIn/booking/scheduleCheckup/ScheduleHourPicker";
import ScheduleSlotPicker from "../pages/loggedIn/booking/scheduleCheckup/ScheduleSlotPicker";
import submitData from "../../controllers/request/submitData";
import LoadingSection from "../helperComponents/LoadingSection";
import TitleBar from "../helperComponents/TitleBar";
import PrimaryButton from "../helperComponents/PrimaryButton";
import BookingDateOptions from "../pages/loggedIn/booking/scheduleCheckup/BookingDateOptions";
import goTo from "../../controllers/goTo";
import { serverLine } from "../../controllers/network/serverLine";
import transformToIdValuePair from "../../controllers/utils/transformToIdValuePair";
import ActivityRechargeBillLine from "../pages/loggedIn/admin/purchaseRequests/ActivityRechargeBillLine";
import getAfterDiscountPrice from "../../controllers/getAfterDiscountPrice";
import Context from "../../Context";
import SecondaryText from "../detailsPage/SecondaryText";
import CheckoutPopup from "../purchase/CheckoutPopup";
import formatNumber from "../../controllers/utils/formatNumber";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 25px;
  padding: 25px;
  background-color: var(--surface);

  @media (max-width: 900px) {
    padding: 0;
    justify-content: flex-start;
    height: calc(100dvh - var(--safe-area-inset-top));
  }
`;

const Inputs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 50px;
  margin-bottom: 20px;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;

  @media (max-width: 900px) {
    padding-bottom: 100px;
    /* position: fixed;
    bottom: 0;
    padding-bottom: 70px;
    padding-top: 40px;
    left: 0;
    width: 100vw; */
  }
`;

const MainSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  border-radius: 10px;

  /* height: 50vh; */
  padding: 20px;
  padding-bottom: 50px;
  @media (max-width: 900px) {
    width: 90vw;
    height: auto;
    border: none;
  }
`;

export default function BookingCreationPopup({
  activityType,
  onSuccess,
  participationCountType,
}) {
  const { loggedInUser, membershipPlans } = useContext(Context);

  const [detailedSelected, setDetailsSelected] = useState(false);
  const [hasMembershipForActivity, setHasMembershipForActivity] =
    useState(true);
  const [activityTypes, setActivityTypes] = useState(null);

  const [date, setDate] = useState(null);
  const [hour, setHour] = useState(null);
  const [slot, setSlot] = useState(null);
  const [loading, setLoading] = useState(true);

  const [amount, setAmount] = useState(0);
  const [discountedAmount, setDiscountedAmount] = useState(0);

  useEffect(() => {
    if (amount && membershipPlans) {
      let newVal = getAfterDiscountPrice({
        total: amount,
        user: loggedInUser,
        membershipPlans,
      });

      setDiscountedAmount(newVal);
    }
  }, [amount, membershipPlans]);

  useEffect(() => {
    serverLine.get("/activity-types").then(setActivityTypes);
  }, []);

  useEffect(() => {
    serverLine
      .get(`/has-membership-for-activity/?activityTypeID=${activityType._id}`)
      .then((newData) => {
        setHasMembershipForActivity(newData);
        setLoading(false);
      });
  }, []);

  const { t } = useTranslation();

  let titleBar = <TitleBar>{activityType.title}</TitleBar>;

  if (loading || !activityTypes)
    return (
      <Container>
        {titleBar}
        <LoadingSection />
      </Container>
    );

  let activityTypesObj = transformToIdValuePair(activityTypes);

  if (detailedSelected) {
    return (
      <Container>
        {titleBar}

        <MainSection>
          <CheckoutPopup
            totalAmount={discountedAmount}
            onCashSelection={cashSelected}
            purchaseDetails={
              <>
                <ActivityRechargeBillLine
                  setAmount={setAmount}
                  type={participationCountType}
                  typeIdVsData={activityTypesObj}
                  quantity={1}
                  itemID={activityType._id}
                />

                {discountedAmount !== amount ? (
                  <SecondaryText>
                    {t("discountedPrice")} = {formatNumber(discountedAmount)}
                  </SecondaryText>
                ) : null}
              </>
            }
          />
        </MainSection>
      </Container>
    );
  }

  return (
    <Container>
      {titleBar}

      <MainSection>
        <Inputs>
          <BookingDateOptions
            activityType={activityType}
            value={date}
            onChange={setDate}
            label={t("selectDate")}
          />

          {date ? (
            <>
              <ScheduleHourPicker
                rangeData={activityType}
                value={hour}
                onChange={setHour}
                label={t("selectHour")}
              />
            </>
          ) : null}

          {date && hour ? (
            <>
              <ScheduleSlotPicker
                activityType={activityType}
                date={date}
                hour={hour}
                value={slot}
                onChange={setSlot}
                label={t("selectSlot")}
              />
            </>
          ) : null}
        </Inputs>
      </MainSection>

      <Buttons>
        <PrimaryButton onClick={promptPayment}>{t("submit")}</PrimaryButton>
      </Buttons>

      <br />
      <br />
      <br />
    </Container>
  );

  async function cashSelected({ onSuccess, onFailure }) {
    try {
      await onSubmit(true);
      onSuccess();
    } catch (e) {
      onFailure();
    }
  }

  async function promptPayment() {
    if (!hour || !date || !slot)
      return window.popupAlert(t("allFieldsAreRequired"));

    if (hasMembershipForActivity) {
      await onSubmit(false, "Booking Done");
      goTo(-1)();
    } else {
      setDetailsSelected(true);
    }
  }

  async function onSubmit(withoutLoading, successMessage) {
    let loadingFunc = withoutLoading ? null : setLoading;

    console.log("loadingFunc", loadingFunc);

    await submitData({
      onSuccessMessage: successMessage,
      method: "post",
      path: "/activity-booking",
      data: {
        date,
        hour,
        slot,
        activityTypeID: activityType._id,
        participationCountType,
        action: "BOOK",
      },
      onSuccess: async () => {
        onSuccess();
        setLoading(false);

        document.body.scrollTop = 0;
        window.scrollTo(0, 0);
        // window.location.reload();
      },
      setLoading: loadingFunc,
    });
  }
}
